import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TicketLayout } from "@jambushrushti/components/tickets/TicketLayout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  useTicketCategories,
  useSaveTicket,
} from "@jambushrushti/hooks/useTickets";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Plus, X, Upload } from "lucide-react";
import { toast } from "sonner";

export default function CreateTicket() {
  const navigate = useNavigate();
  const { data: categories = [] } = useTicketCategories();
  const saveTicket = useSaveTicket();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category_id: "",
    sub_category: "",
    priority: "medium" as "low" | "medium" | "high" | "critical",
    tags: [] as string[],
    attachment_url: "",
  });

  const [tagInput, setTagInput] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Please enter a ticket title");
      return;
    }

    try {
      await saveTicket.mutateAsync({
        ...formData,
        category_id: formData.category_id || null,
      });
      navigate("/tickets/list");
    } catch (error) {
      // Error handled by hook
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  return (
    <TicketLayout title="Create Ticket">
      <Seo
        title="Create Ticket | Jambushrusti"
        description="Create a new support ticket for the Jambushrusti platform."
      />

      <div className="mx-auto max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>Create New Ticket</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Ticket Title *</Label>
                <Input
                  id="title"
                  placeholder="Brief summary of the issue"
                  value={formData.title}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, title: e.target.value }))
                  }
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Detailed description of the issue..."
                  rows={5}
                  value={formData.description}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Category and Priority */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={value =>
                      setFormData(prev => ({ ...prev, category_id: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority *</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={value =>
                      setFormData(prev => ({ ...prev, priority: value as any }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sub-category */}
              <div className="space-y-2">
                <Label htmlFor="sub_category">Sub-category (optional)</Label>
                <Input
                  id="sub_category"
                  placeholder="e.g., Login Issue, Access Request..."
                  value={formData.sub_category}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      sub_category: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label>Tags (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a tag..."
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e =>
                      e.key === "Enter" && (e.preventDefault(), addTag())
                    }
                  />
                  <Button type="button" variant="outline" onClick={addTag}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="gap-1">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Attachment URL */}
              <div className="space-y-2">
                <Label htmlFor="attachment">Attachment URL (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    id="attachment"
                    placeholder="https://..."
                    value={formData.attachment_url}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        attachment_url: e.target.value,
                      }))
                    }
                  />
                  <Button type="button" variant="outline" disabled>
                    <Upload className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Paste a URL to an attachment or screenshot
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saveTicket.isPending}>
                  {saveTicket.isPending ? "Creating..." : "Create Ticket"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </TicketLayout>
  );
}
